"use client";

import { useState } from "react";

export function LoginForm({ needsSetup }: { needsSetup: boolean }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    // Fonte de verdade é o formulário, não o estado React. Gerenciador de senha
    // e autofill do navegador escrevem direto no valor do input sem disparar o
    // `onChange` do componente controlado: os três campos aparecem preenchidos
    // na tela enquanto o estado ainda carrega o valor antigo, e o painel envia
    // uma senha diferente da que você digitou.
    const form = new FormData(event.currentTarget);
    const sentUsername = String(form.get("username") ?? "").trim();
    const sentPassword = String(form.get("password") ?? "");
    const sentConfirmation = String(form.get("password-confirmation") ?? "");

    if (!sentUsername || !sentPassword) {
      setError("Preencha o usuário e a senha.");
      return;
    }
    if (needsSetup && sentPassword !== sentConfirmation) {
      setError("As duas senhas não são iguais.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/session", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          action: needsSetup ? "setup" : "login",
          username: sentUsername,
          password: sentPassword,
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(payload.error || "Não foi possível entrar.");

      // Ler o corpo antes de navegar não é detalhe: sem isso a navegação corre
      // com o fim da resposta, o `Set-Cookie` pode não ter sido aplicado ainda,
      // e o usuário entra com a senha certa e volta para a tela de login.
      window.location.replace("/");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível entrar.");
      setBusy(false);
    }
  }

  return (
    <form className="login-form" onSubmit={submit}>
      <label htmlFor="panel-username">Usuário</label>
      <input
        id="panel-username"
        name="username"
        type="text"
        autoComplete="username"
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        required
      />

      <label htmlFor="panel-password">Senha</label>
      <input
        id="panel-password"
        name="password"
        type="password"
        autoComplete={needsSetup ? "new-password" : "current-password"}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        required
      />

      {needsSetup ? (
        <>
          <label htmlFor="panel-password-confirmation">Repita a senha</label>
          <input
            id="panel-password-confirmation"
            name="password-confirmation"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            required
          />
          <small>Pelo menos 10 caracteres, com letras e números.</small>
        </>
      ) : null}

      {/* Só `busy` desabilita: com autofill o estado pode estar vazio enquanto
          os campos estão preenchidos, e o botão ficaria travado sem motivo. O
          `required` de cada input faz a validação nativa. */}
      <button className="button button-primary" type="submit" disabled={busy}>
        {busy ? "Entrando…" : needsSetup ? "Definir senha e entrar" : "Entrar"}
      </button>

      <p className="login-error" role="alert" aria-live="polite">
        {error}
      </p>
    </form>
  );
}
